/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-13 17:17:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { Card, Icon, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { xssSanitizeInput } from 'src/helpers';

import {
  DesktopMajor,
  MobileMajor,
  SidebarLeftMajor,
  SectionMajor,
  QuestionMarkMajor
} from '@shopify/polaris-icons';
import { generateFakedData, buildFilterOptionValue } from 'src/helpers/filter-tree-option';

type Props = {
  onDismiss: Function,
  formFilterOptionValues: Object
};

type ComposedProps = Props & WithI18nProps;

class Preview extends Component<ComposedProps, State> {
  state = {
    showMessage: true,
    showPreviewSoon: false,
    selectedDevice: 'desktop', // 'desktop', 'mobile'
    selectedLayout: 'vertical' // 'vertical', 'horizontal'
  };

  componentDidMount() {
    const { formFilterOptionValues } = this.props;

    // Render preview content
    this.renderPreview(formFilterOptionValues);

    // Recall render preview to ensure that the filter option style is consistent with preview layout
    setTimeout(() => {
      this.renderPreview(formFilterOptionValues);
    }, 500);
  }

  componentWillReceiveProps(nextProps) {
    const { formFilterOptionValues } = nextProps;

    // <!> Render preview content
    // Wait a little bit for all state updating since multi-level values are updated through reducer
    setTimeout(() => {
      this.renderPreview(formFilterOptionValues);
    }, 200);
  }

  renderPreview = formFilterOptionValues => {
    const { optionType } = formFilterOptionValues;
    // const { selectedDevice } = this.state;

    if (_.isEmpty(formFilterOptionValues) || !optionType) return;
    // Build filter option
    let formValues = this.assignMultiLevelValues(formFilterOptionValues);
    let filterOption = buildFilterOptionValue(formValues);
    filterOption = generateFakedData(filterOption);

    // Check xss data
    filterOption = xssSanitizeInput(filterOption);
    filterOption.values =
      filterOption?.values && _.isArray(filterOption?.value)
        ? filterOption?.values?.filter(v => v?.key !== '')
        : filterOption.values;
    try {
      window.boostBuildFilterOptionPreview([filterOption], false);
    } catch (error) {
      console.log('Error: Render preview', error);
      console.log(filterOption);
    }
    this.setState({ showMessage: false, showPreviewSoon: false });
  };

  checkIsMultiLevelOption = filterOption => {
    const { filterType } = filterOption;
    const { multiLevelTagCollection } = this.props;

    return (
      multiLevelTagCollection && (filterType === 'multi_level_tag' || filterType === 'collection')
    );
  };

  assignMultiLevelValues = formValues => {
    const cloned = _.cloneDeep(formValues);
    const { optionType, collectionDisplayType } = formValues;
    const { multiLevelTagManualValues, collectionMultiLevelManualValues } = this.props;

    if (optionType === 'multi_level_tag')
      cloned.multiLevelTagManualValues = multiLevelTagManualValues;

    if (optionType === 'collection' && collectionDisplayType === 'multi_level_collections')
      cloned.collectionMultiLevelManualValues = collectionMultiLevelManualValues;

    return cloned;
  };

  render() {
    const { showMessage, selectedDevice, selectedLayout } = this.state;
    const { onDismiss, i18n } = this.props;

    // const previewSoonUrl = ''

    let previewInner =
      selectedDevice === 'desktop'
        ? 'bc-sf-filter-preview-inner'
        : 'bc-sf-filter-preview-inner bc-sf-filter-mobile-wrapper';
    previewInner += selectedLayout === 'vertical' ? ' overflow-vertical' : ' overflow-horizontal';

    return (
      <div className="previewPanel">
        <Card
          title={i18n.translate('AdminHTML.FilterTree.Common.preview')}
          sectioned
          actions={[
            {
              content: i18n.translate('AdminHTML.Common.Action.hide'),
              onAction: () => {
                onDismiss();
              }
            }
          ]}>
          <div className="device">
            <div>{i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_Device')}</div>
            <div className="icons">
              <Tooltip
                content={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_Preview_TooltipPC_Content'
                )}
                preferredPosition="above">
                <span
                  className={selectedDevice === 'desktop' ? 'icon active first' : 'icon first'}
                  onClick={() => this.changeDevice('desktop')}>
                  <Icon
                    source={DesktopMajor}
                    color={selectedDevice === 'desktop' ? 'primary' : 'base'}
                  />
                </span>
              </Tooltip>
              <Tooltip
                content={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_Preview_TooltipMobile_Content'
                )}
                preferredPosition="above">
                <span
                  className={selectedDevice === 'mobile' ? 'icon active' : 'icon'}
                  onClick={() => this.changeDevice('mobile')}>
                  <Icon
                    source={MobileMajor}
                    color={selectedDevice === 'mobile' ? 'primary' : 'base'}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
          <div className="layout">
            <div>{i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_Layout')}</div>
            <div className="icons">
              <Tooltip
                content={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_Preview_TooltipVertical_Content'
                )}
                preferredPosition="above">
                <span
                  className={selectedLayout === 'vertical' ? 'icon active first' : 'icon first'}
                  onClick={() => this.changeLayout('vertical')}>
                  <Icon
                    source={SidebarLeftMajor}
                    color={selectedLayout === 'vertical' ? 'primary' : 'base'}
                  />
                </span>
              </Tooltip>
              {/* Do not allow to switch to horizontal layout for mobile */}
              {selectedDevice === 'mobile' ? (
                <Tooltip
                  content={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_Preview_TooltipHorizontal_Content'
                  )}
                  preferredPosition="above">
                  <span className="icon" style={{ opacity: '30%' }} onClick={() => {}}>
                    <Icon source={SectionMajor} color="primary" />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  content={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_Preview_TooltipHorizontal_Content'
                  )}
                  preferredPosition="above">
                  <span
                    className={selectedLayout === 'horizontal' ? 'icon active' : 'icon'}
                    onClick={() => this.changeLayout('horizontal')}>
                    <Icon
                      source={SectionMajor}
                      color={selectedLayout === 'horizontal' ? 'primary' : 'base'}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </Card>
        {showMessage ? (
          <div className="message">
            {i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_Message')}
          </div>
        ) : (
          ''
        )}

        {/*showPreviewSoon ? (
          <div className="previewSoon">
            {i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_PreviewSoon_Content', {
              t: (
                <Link url={previewSoonUrl} external>
                  {i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_PreviewSoon_Content_t')}
                </Link>
              )
            })}
          </div>
        ) : (
          ''
        )*/}

        <div style={showMessage ? { display: 'none' } : { display: 'block' }}>
          <div className="filterTree">
            <div className={previewInner}>
              {/* Preview for mobile */}
              <div
                className="boost-pfs-filter-tree-mobile-wrap"
                style={
                  selectedDevice === 'mobile'
                    ? { display: 'block', marginTop: '-24px' }
                    : { display: 'none' }
                }>
                <div className="boost-pfs-filter-mobile-toolbar-items">
                  <div className="boost-pfs-filter-mobile-toolbar-left">
                    <a
                      href="https://"
                      className="boost-pfs-filter-back-btn"
                      rel="noopener noreferrer"
                      style={{ pointerEvents: 'none' }}>
                      {''}
                    </a>
                  </div>
                  <div className="boost-pfs-filter-mobile-toolbar-middle">
                    {i18n.translate('AdminHTML.FilterTree.Common.collection')}
                  </div>
                </div>

                <div className="boost-pfs-filter-tree-content-wrapper">
                  <div className="boost-pfs-filter-tree boost-pfs-filter-tree-v"></div>
                </div>

                <div className="boost-pfs-filter-mobile-content-footer">
                  <button
                    className="boost-pfs-filter-show-result"
                    style={{ textTransform: 'capitalize' }}>
                    {i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_ShowResult')}
                  </button>
                </div>
              </div>

              {/* Preview for desktop, layout vertical */}
              <div
                className="boost-pfs-filter-tree boost-pfs-filter-tree-v"
                style={
                  selectedDevice === 'desktop' && selectedLayout === 'vertical'
                    ? { display: 'block' }
                    : { display: 'none' }
                }>
                <span className="preview-not-available">
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_Preview_PreviewNotAvailable'
                  )}
                </span>
              </div>

              {/* Preview for desktop, layout horizontal */}
              <div
                className="boost-pfs-filter-tree boost-pfs-filter-tree-h"
                style={
                  selectedLayout === 'vertical' || selectedDevice === 'mobile'
                    ? { display: 'none' }
                    : {}
                }>
                <span className="preview-not-available">
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_Preview_PreviewNotAvailable'
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="explanProductNumbers">
            <span>
              {i18n.translate('AdminHTML.FilterTree.Item.optionModal_Preview_Note')}
              <span className="tooltip long-tooltip">
                <Tooltip
                  content={
                    <span className="tooltipContent">
                      <ol style={{ paddingLeft: '1.25rem' }}>
                        <li>
                          {i18n.translate(
                            'AdminHTML.FilterTree.Item.optionModal_Preview_Note_Tooltip_Li1'
                          )}
                        </li>
                        <li>
                          {i18n.translate(
                            'AdminHTML.FilterTree.Item.optionModal_Preview_Note_Tooltip_Li2'
                          )}
                        </li>
                      </ol>
                    </span>
                  }
                  preferredPosition="above">
                  <Icon source={QuestionMarkMajor} color="base" />
                </Tooltip>
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  changeDevice = selectedDevice => {
    const updatedState = { selectedDevice };

    // If device is mobile, switch layout to vertical
    if (selectedDevice === 'mobile') {
      updatedState.selectedLayout = 'vertical';
    }

    this.setState(updatedState);
  };

  changeLayout = selectedLayout => {
    this.setState({ selectedLayout });
  };
}

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);

  // Get multi level tag from store
  const multiLevelTags = _.get(state.multiLevelFilterOption, 'multiLevelTags', {});

  // Get multi level tag from store
  const multiLevelCollections = _.get(state.multiLevelFilterOption, 'multiLevelCollections', {});

  return {
    loadingDataByType: _.get(state.filterData, 'loadingDataByType', false),
    multiLevelTagManualValues: _.get(multiLevelTags, 'multiLevelTagManualValues', []),
    collectionMultiLevelManualValues: _.get(
      multiLevelCollections,
      'collectionMultiLevelManualValues',
      []
    ),
    formFilterOptionValues
  };
};

export default connect(mapStateToProps)(withI18n()(Preview));
